import { OverlayApp } from "@cuekards/ui";
import type { MediaStreamLike } from "@cuekards/ui";
import { BACK_TO_SCRIPT } from "./control-bar.js";
import { backdropStyle, topBarButtons } from "./prompter.js";
import { useSelfView } from "./self-view.js";
import { HowItWorksButton } from "./site-links.js";

/**
 * The prompter screen (web-ui ticket 08): the script over your own face.
 *
 * Until this ticket the tab's prompter was `OverlayApp` on a flat near-black
 * page. The camera had been open since ticket 04 and the green room had been
 * showing it to you since ticket 05 — and then you stepped through the door and
 * it went away, which is the one screen where a teleprompter beside a lens most
 * wants a frame in it. Frames 1c and 1g put it back.
 *
 * **Everything here is a wrapper, and that is the rule the whole file lives
 * under.** `OverlayApp` is `@cuekards/ui` and the desktop's overlay mounts the
 * same component; `packages/browser-gate` measures the prompter inside it
 * against a baseline. So this is a video element behind, a scrim between, and
 * chrome above — and not one rule, prop or line reaches inside. The reading
 * surface is the product: the anchor, the easing, the word colouring and the
 * memoised lines are finished, and a restyle that reached in there would be a
 * restyle of the desktop app and of the gate's baseline at the same time.
 *
 * Two things in particular will fight whoever forgets that. `OverlayApp` writes
 * `.overlay-block`'s padding from JS as `height x ANCHOR_FRACTION`, so a CSS
 * rule that adds padding there moves the anchor; and `.overlay` must keep
 * resolving to a definite `100vh`, because `.overlay-scroll` inside it is
 * `position: absolute; inset: 0`. That is why the backdrop is `position: fixed`
 * rather than a flex parent wrapped around the prompter — a wrapper that took
 * `.overlay` out of normal flow would give it no height at all, and the symptom
 * is a blank screen rather than an error.
 *
 * The layering is DOM order rather than a `z-index` on the prompter, for the
 * same reason: the backdrop is a fixed layer at `z-index: 0` and everything
 * after it in this tree paints over it. `.overlay` is never named by a selector
 * or given a style, here or in `web.css`.
 */

export function PrompterScreen({
  stream,
  backdrop,
  anchor,
  idle,
  takeInProgress,
  onEdit,
  onSettings,
}: {
  /**
   * The self-view, or null when there is no picture to show.
   *
   * The host's `useCapture` stream, mounted above all three screens — the same
   * object the green room's stage is drawing and the same one a take borrows
   * rather than reopening. Nothing here calls `getUserMedia`, and that is what
   * keeps FR-31's "the camera is opened exactly once" a property of the code:
   * a second request from this screen would close and reopen the device at the
   * moment the countdown ends.
   *
   * Null is an ordinary state and not a failure — the camera switched off, a
   * machine with none, a permission not given — and what it leaves is the flat
   * page the prompter had before this ticket, which reads correctly.
   */
  stream: MediaStreamLike | null;
  /** The Backdrop setting, 0..90. Live: the host follows the settings store, so
   * moving the slider on the other screen changes this one without a reload. */
  backdrop: number;
  /**
   * Where the reading line sits inside `OverlayApp`, so the scrim's band can sit
   * where the eye is (text-align ticket 01).
   *
   * The one number this file takes *about* the prompter rather than around it,
   * and it does not break the rule above: nothing here reaches in to read it.
   * The host holds it, `OverlayApp` draws itself from it through the same view
   * state, and this draws the layer behind from the same value — which is what
   * keeps the band and the words in one place rather than two that agree most of
   * the time.
   */
  anchor: number;
  /** Whether the screen has gone idle — the host's one `useIdleFade`, shared
   * with the control bar so the two pieces of chrome recede together. */
  idle: boolean;
  takeInProgress: boolean;
  onEdit: () => void;
  onSettings: () => void;
}): JSX.Element {
  const buttons = topBarButtons({ takeInProgress });
  return (
    <>
      {/* The one screen in the product that had no heading of any kind (seo
        * ticket 08) — the room at least had a wordmark. A sibling of `OverlayApp`
        * and not a word inside it: this file's rule is that nothing reaches in
        * there, and the reading surface belongs to the desktop and to the gate's
        * baseline as much as to this page. Visually hidden, because a title over a
        * script being read to camera is the one thing this screen is for not
        * having. */}
      <h1 className="web-visually-hidden">The prompter</h1>
      <Backdrop stream={stream} backdrop={backdrop} anchor={anchor} />
      {/* Mounted exactly as it was, which is the point. */}
      <OverlayApp />
      <div className={idle ? "web-prompter-top is-faded" : "web-prompter-top"}>
        <div className="web-prompter-top-left">
          {/* The one pill on this bar since flow ticket 07.
            *
            * A `⟳ Refresh` pill stood beside it and is gone: refresh is FR-17 and
            * keeps its `⋯` row, its keyboard chord and all four of
            * `refreshAvailability`'s states — what it does not need is a second
            * button for a thing you do between takes, on the one piece of chrome
            * that is live while a camera is pointed at you.
            *
            * The words are {@link BACK_TO_SCRIPT}, the same constant the menu's
            * `edit` row reads, because the pill and the row are one door. The `←`
            * is `aria-hidden` decoration and not part of the label: it says which
            * way the press goes, which is a thing the words alone do not. */}
          <button
            type="button"
            className="web-prompter-pill web-prompter-edit"
            onClick={onEdit}
            disabled={buttons.edit.disabled}
            title={buttons.edit.reason}
          >
            <span aria-hidden="true">←</span> {BACK_TO_SCRIPT}
          </button>
        </div>
        {/* One link and one circle, in the corner the `?` and the `⚙` have shared
          * on every screen — which are also the handles the browser drivers press
          * them by.
          *
          * **The `?` is an anchor now** (flow ticket 02), pointing at the same
          * `/how-it-works` the room's bar and every document's bar point at.
          * `HelpPage` was a third rendering of copy that already exists at an
          * address, and this is the press that used to open it.
          *
          * It is also the press this arrangement was hardest for, and the reason
          * `HowItWorksButton` is a shared component rather than markup written
          * out on each screen: this bar is live *during a take*, so the link can
          * be pressed with a recording running behind it, and it is the
          * `target="_blank"` inside that component that keeps the take.
          * [ADR-0025](../../docs/adr/0025-the-site-and-the-app-are-different-pages.md)
          * kept help a screen for exactly that reason. A new tab answers it —
          * the take keeps rolling, and nothing about this tab moves.
          *
          * **A row lower here than on the other two, and that is the badge's
          * doing.** `OverlayApp` pins its recording badge in this exact corner
          * and carries no `z-index`, so this bar covered its timecode for the
          * whole of every take — eighty pixels of it, measured. The badge is
          * FR-49 and it is not this ticket's to move, so the layer gives way and
          * starts under it; see `.web-prompter-top` in `web.css` for the number
          * and the argument. */}
        <div className="web-prompter-top-right">
          <HowItWorksButton />
          <button
            type="button"
            className="web-head-button web-settings-link"
            onClick={onSettings}
            title="Settings"
            aria-label="Settings"
          >
            ⚙
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * The frame, and the scrim that makes words readable over it.
 *
 * `aria-hidden` and `pointer-events: none` throughout: it is a backdrop in the
 * literal sense — nothing in it is a control, and nothing in it is worth
 * announcing to somebody who is not looking at it.
 *
 * **Mirrored, and the recording is not.** The flip is a CSS transform on the
 * element and the stream handed to `MediaRecorder` is the same object with
 * nothing between it and the recorder — the same arrangement the green room's
 * stage is on, and the same distinction that is impossible to notice until
 * somebody reads a sign on camera.
 */
function Backdrop({
  stream,
  backdrop,
  anchor,
}: {
  stream: MediaStreamLike | null;
  backdrop: number;
  anchor: number;
}): JSX.Element {
  const video = useSelfView(stream);

  return (
    <div className="web-prompter-backdrop" aria-hidden="true">
      <video
        className="web-prompter-video"
        ref={video}
        muted
        playsInline
        // A picture of you, not a monitor of the take: its own audio would be
        // your voice a fraction of a second late, in front of a microphone.
        autoPlay
      />
      {/* The strength is a setting and the shape is not, so what crosses this
        * boundary is four percentages — two strengths and the two edges of the
        * band, which follow the anchor — and the gradient itself stays in the
        * stylesheet where its geometry is readable. See `backdropStyle`. */}
      <div
        className="web-prompter-scrim"
        /* The cast is the one React makes necessary: `CSSProperties` has no
         * index signature for custom properties, and widening `BackdropStyle`
         * into one would give up the names being checked at all. `OverlayApp`
         * casts `--overlay-font-size` the same way, for the same reason. */
        style={backdropStyle(backdrop, anchor) as React.CSSProperties}
      />
    </div>
  );
}
